/**
 * QUIPWA — DeckHealthCard (Simplificado)
 * 
 * Vista resumida para el Dashboard.
 * Muestra el estado crítico (pendientes) y metadatos básicos.
 */

import type { DeckInstance, DeckStats } from '../../types/domain.ts'
import { useI18n, type TFunction } from '../../lib/i18n/i18n-store'
import type { Locale } from '../../lib/i18n/translations'
import { DrawerReadouts } from './DrawerReadouts'

import './DeckHealthCard.css'
interface DeckHealthCardProps {
  instance: DeckInstance
  stats: DeckStats | null
  onClick?: () => void
  onStudy?: () => void
}

function formatDate(ts: number | null, t: TFunction, locale: Locale): string {
  if (!ts) return t('dashboard.notStudied')
  const d = new Date(ts * 1000)
  const now = Date.now()
  const diff = now - d.getTime()
  const days = Math.floor(diff / 86400000)
  if (days === 0) return t('deck.today')
  if (days === 1) return t('deck.yesterday')
  if (days < 7) return t('deck.daysAgo', { n: days })
  return d.toLocaleDateString(locale, { month: 'short', day: 'numeric' })
}

export function DeckHealthCard({ instance, stats, onClick, onStudy }: DeckHealthCardProps) {
  const { t, locale } = useI18n()
  const due = (stats?.due_today ?? 0) + (stats?.overdue_cards ?? 0)
  const isNew = (stats?.new_cards ?? 0) > 0
  const hasWork = due > 0 || isNew
  
  const retention = stats?.avg_retention != null
    ? t('dashboard.retention', { value: Math.round(stats.avg_retention * 100) })
    : null

  return (
    // .drawer: a skin draws it as a drawer front in the deck's colour.
    <div
      className={`deck-card drawer${hasWork ? ' has-work' : ''}`}
      style={instance.deck_color ? { '--deck-color': instance.deck_color } as React.CSSProperties : undefined}
    >
      {/* Header: Nombre + Badge de urgencia.
          The name is the button: opening the deck was previously a div onClick,
          unreachable by keyboard, while the only focusable control (Study) went
          somewhere else entirely. */}
      <div className="deck-card-header">
        <button className="deck-card-open" onClick={onClick}>
          <span className="deck-card-name">{instance.deck_name}</span>
        </button>
        {due > 0 && (
          <span className="chip deck-due-badge">
            {t(due === 1 ? 'deck.pending' : 'deck.pendingPlural', { count: due })}
          </span>
        )}
      </div>

      <DrawerReadouts stats={stats} />

      {/* Meta row. Retention leads and is the only emphasised value here: it's
          how the user sees FSRS actually working, so it shouldn't read as
          trivia sitting between a card count and a date. */}
      <div className="deck-meta-row">
        {retention ? (
          <span className="deck-retention">{retention}</span>
        ) : (
          <span className="deck-meta-item deck-meta-item--muted">
            {t('dashboard.notStudied')}
          </span>
        )}
        <span className="deck-meta-dot">·</span>
        <span className="deck-meta-item">
          {t('deck.cards', { count: instance.card_count })}
        </span>
        {stats?.last_studied_at && (
          <>
            <span className="deck-meta-dot">·</span>
            <span className="deck-meta-item">
              {formatDate(stats.last_studied_at, t, locale)}
            </span>
          </>
        )}
      </div>

      {/* Action: El botón solo aparece si hay algo que hacer (due o nuevas) */}
      {hasWork && onStudy && (
        <button className="btn btn--soft deck-study-btn" onClick={onStudy}>
          {due > 0 ? `${t('dashboard.reviewNow')} →` : `${t('dashboard.learnNew')} →`}
        </button>
      )}

    </div>
  )
}